import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { auth } from '@lib/firebase'

const FirebaseAuthContext = createContext(null)

export function FirebaseAuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const applyUser = (next) => {
      if (!cancelled) {
        setUser(next)
        setLoading(false)
      }
    }

    // Ensures persistence (IndexedDB) has been read before first paint logic
    const readyPromise =
      typeof auth.authStateReady === 'function'
        ? auth.authStateReady().catch(() => {})
        : Promise.resolve()
    void readyPromise.then(() => {
      if (!cancelled) applyUser(auth.currentUser)
    })

    const unsub = onAuthStateChanged(auth, applyUser)
    return () => {
      cancelled = true
      unsub()
    }
  }, [])

  const value = useMemo(
    () => ({
      user,
      loading,
      signOutUser: () => signOut(auth)
    }),
    [user, loading]
  )

  return <FirebaseAuthContext.Provider value={value}>{children}</FirebaseAuthContext.Provider>
}

export function useFirebaseAuth() {
  const ctx = useContext(FirebaseAuthContext)
  if (ctx == null) {
    throw new Error('useFirebaseAuth must be used within FirebaseAuthProvider')
  }
  return ctx
}
